import { CoreSegmentCell } from "pro-features/components/core cells/src/CoreSegmentCell/CoreSegmentCell";
import { CoreWebsiteCell } from "pro-features/components/core cells/src/CoreWebsiteCell/CoreWebsiteCell";
import {
    BounceRate,
    DefaultCellRightAlign,
    HeatmapCell,
    IndexCell,
    TrafficShare,
} from "app/components/React/Table/cells";
import { RowSelectionConsumer } from "app/components/React/Table/cells/RowSelection";
import { DefaultCellHeader, DefaultCellHeaderRightAlign } from "app/components/React/Table/headerCells";
import { i18nFilter } from "app/filters/i18nFilter";
import { SegmentCellContainer } from "app/pages/conversion/conversionTableContainer/StyledComponents";
import { itsMyOwnSegment } from "app/pages/segments/analysis/itsMyOwnSegment";
import { ChannelsObj } from "app/pages/segments/mmx/ChannelsObj";
import { EngagementVerticals } from "app/pages/segments/mmx/EngagementVerticals";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { SEGMENT_TYPES } from "app/services/segments/segmentsApiService";
import { SwTrack } from "app/services/SwTrack";
import { allTrackers } from "app/services/track/track";

import { wrapCellComponentDisabled } from "../analysis/segmentsGroupsAnalysisTableContainer/SegmentsGroupsAnalysisTableConfig";

const getHeatmapColumn = ({
    field,
    displayName,
    isPercentage,
}: {
    field: string;
    displayName: string;
    isPercentage: boolean;
}) => {
    return {
        field,
        displayName,
        cellComponent: wrapCellComponentDisabled(HeatmapCell),
        headerComponent: DefaultCellHeaderRightAlign,
        replaceZeroValue: "-",
        CellValueFormat: isPercentage ? "smallNumbersPercentage:1" : "abbrNumber",
        sortable: false,
        minWidth: 72,
        hideZeroValue: false,
        visible: true,
    };
};

export const segmentsGroupMMXTableColumnsConfig = (isPercentage, selectedmetric) => {
    const baseColumns = [
        {
            fixed: true,
            isCheckBox: true,
            cellComponent: RowSelectionConsumer,
            sortable: false,
            width: 33,
            headerComponent: DefaultCellHeader,
            visible: true,
            disableHeaderCellHover: true,
        },
        {
            fixed: true,
            displayName: "#",
            cellComponent: IndexCell,
            headerComponent: DefaultCellHeader,
            disableHeaderCellHover: true,
            sortable: false,
            width: 40,
            visible: true,
        },
        {
            fixed: true,
            field: "SegmentDomain",
            displayName: i18nFilter()("segment.analysis.marketingChannels.group.table.column.segment"),
            type: "string",
            format: "None",
            isResizable: true,
            sortable: false,
            isSorted: false,
            cellComponent: ({ value, row }) => {
                const subtitleFilters = [];
                const editSegmentClick = () => {
                    SwTrack.all.trackEvent("Internal Link", "click", `segmentCard/editSegment/${row.id}`);
                    const currentModule = proNavigator.getCurrentModule();
                    const segmentWizardStateName = `${currentModule}-wizard`;
                    proNavigator.go(segmentWizardStateName, { sid: row.id });
                };
                if (row.SegmentType === SEGMENT_TYPES.SEGMENT) {
                    subtitleFilters.push({
                        filter: "text",
                        value: row.segmentName,
                    });
                }
                return (
                    <SegmentCellContainer withSegment={row.SegmentType === SEGMENT_TYPES.SEGMENT}>
                        {row.SegmentType === SEGMENT_TYPES.WEBSITE ? (
                            <CoreWebsiteCell
                                withWebsiteSign={true}
                                icon={row.favicon}
                                domain={row.domain}
                                domainBadge="WEBSITE"
                                externalLink={`http://${row.domain}`}
                                subtitleFilters={subtitleFilters.length > 0 ? subtitleFilters : null}
                                trackExternalLink={() =>
                                    allTrackers.trackEvent("external link", "click", "Segments Group MMX")
                                }
                                hideTrackButton={true}
                            />
                        ) : (
                            <CoreSegmentCell
                                withWebsiteSign={true}
                                segmentName={row.segmentName}
                                lastModified={row.lastUpdated}
                                isOrgSegment={!itsMyOwnSegment(row.userId)}
                                onClick={editSegmentClick}
                                segmentId={row.id}
                                icon={row.favicon}
                                domain={row.domain}
                                externalLink={`http://${value}`}
                                subtitleFilters={subtitleFilters.length > 0 ? subtitleFilters : null}
                                trackExternalLink={() =>
                                    allTrackers.trackEvent("external link", "click", "Segments Group MMX")
                                }
                            />
                        )}
                    </SegmentCellContainer>
                );
            },
            headerComponent: DefaultCellHeader,
            totalCount: true,
            width: 190,
            showTotalCount: false,
            visible: true,
        },
        {
            field: "GroupVisitsShare",
            displayName: i18nFilter()("segment.analysis.marketingChannels.group.table.column.GroupVisitsShare"),
            type: "double",
            format: "percentagesign",
            sortable: true,
            sortDirection: "desc",
            isSorted: false,
            cellComponent: wrapCellComponentDisabled(TrafficShare),
            headerComponent: DefaultCellHeader,
            width: 124,
            visible: true,
        },
        {
            field: "Visits",
            displayName: i18nFilter()("segment.analysis.marketingChannels.group.table.column.visits"),
            type: "double",
            format: "minVisitsAbbr",
            sortable: false,
            isSorted: false,
            isResizable: false,
            cellComponent: wrapCellComponentDisabled(DefaultCellRightAlign),
            headerComponent: DefaultCellHeaderRightAlign,
            visible: true,
            width: 104,
        },
        {
            field: "SegmentShare",
            displayName: i18nFilter()("segment.analysis.marketingChannels.group.table.column.segment.share"),
            type: "double",
            format: "percentagesign",
            sortable: false,
            isResizable: false,
            isSorted: false,
            cellComponent: wrapCellComponentDisabled(BounceRate),
            headerComponent: DefaultCellHeaderRightAlign,
            visible: true,
            width: 104,
        },
    ];
    return selectedmetric === EngagementVerticals.Visits.name
        ? baseColumns.concat(HeatmapCol(isPercentage))
        : baseColumns.concat(channelsCol(selectedmetric));
};

const channelsCol = (selectedMetric): any[] => {
    return Object.keys(ChannelsObj).map((channelKey) => {
        return {
            field: channelKey,
            displayName: i18nFilter()(
                `segment.analysis.marketingChannels.group.table.column.${channelKey}`.toLowerCase(),
            ),
            sortable: false,
            isSorted: false,
            format: EngagementVerticals[selectedMetric].format,
            cellComponent: wrapCellComponentDisabled(EngagementVerticals[selectedMetric].cellComponent),
            headerComponent: DefaultCellHeaderRightAlign,
            visible: true,
            minWidth: 72,
        };
    });
};

const HeatmapCol = (isPercentage): any[] => {
    return [
        getHeatmapColumn({
            field: "Direct",
            displayName: i18nFilter()("segment.analysis.marketingChannels.group.table.column.direct"),
            isPercentage,
        }),
        getHeatmapColumn({
            field: "Mail",
            displayName: i18nFilter()("segment.analysis.marketingChannels.group.table.column.mail"),
            isPercentage,
        }),
        getHeatmapColumn({
            field: "Referrals",
            displayName: i18nFilter()("segment.analysis.marketingChannels.group.table.column.referrals"),
            isPercentage,
        }),
        getHeatmapColumn({
            field: "Social",
            displayName: i18nFilter()("segment.analysis.marketingChannels.group.table.column.social"),
            isPercentage,
        }),
        getHeatmapColumn({
            field: "OrganicSearch",
            displayName: i18nFilter()("segment.analysis.marketingChannels.group.table.column.organicsearch"),
            isPercentage,
        }),
        getHeatmapColumn({
            field: "PaidSearch",
            displayName: i18nFilter()("segment.analysis.marketingChannels.group.table.column.paidsearch"),
            isPercentage,
        }),
        getHeatmapColumn({
            field: "PaidReferrals",
            displayName: i18nFilter()("segment.analysis.marketingChannels.group.table.column.paidreferrals"),
            isPercentage,
        }),
        getHeatmapColumn({
            field: "InternalReferrals",
            displayName: i18nFilter()("segment.analysis.marketingChannels.group.table.column.internalreferrals"),
            isPercentage,
        }),
    ];
};
